/// <reference types="dom-speech-recognition" />

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}
import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { getCachedUser } from '../utils/userCache';
import SimplePeer from 'simple-peer';
import api from '@/services/api';
import socketService from '@/services/socket';
import soundService from '@/services/sounds';
import { useToast } from '@/components/common/ToastProvider';
import { createAudioConstraints } from '../utils/audioConstraints';

interface VoiceUser {
  id: string;
  username: string;
  isMuted: boolean;
  isDeafened: boolean;
}

interface VoiceCallContextType {
  captionsEnabled: boolean;
  captions: Array<{
    userId: string;
    username: string;
    text: string;
    isFinal: boolean;
    timestamp: number;
  }>;
  toggleCaptions: () => void;
  isInCall: boolean;
  channelId: string | null;
  channelName: string | null;
  isMuted: boolean;
  isDeafened: boolean;
  users: VoiceUser[];
  connectionQuality: 'good' | 'medium' | 'poor';
  joinVoiceChannel: (channelId: string, channelName: string) => Promise<void>;
  leaveVoiceChannel: () => Promise<void>;
  toggleMute: () => Promise<void>;
  toggleDeafen: () => Promise<void>;
  localStream: MediaStream | null;
  isDnd: boolean;
  toggleDnd: (enable?: boolean) => void;
  addScreenTrack?: (stream: MediaStream) => void;
  removeScreenTrack?: () => void;
  reconnectingUsers?: Record<string, boolean>;
}

const VoiceCallContext = createContext<VoiceCallContextType | undefined>(undefined);

export function useVoiceCall() {
  const context = useContext(VoiceCallContext);
  if (!context) {
    throw new Error('useVoiceCall must be used within VoiceCallProvider');
  }
  return context;
}

const RECONNECT_TIMEOUT_MS = 10000; // how long to wait for socket to reconnect before leaving (10s)

export function VoiceCallProvider({ children }: { children: ReactNode }) {
  const [isInCall, setIsInCall] = useState(false);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [channelName, setChannelName] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [users, setUsers] = useState<VoiceUser[]>([]);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [connectionQuality, setConnectionQuality] = useState<'good' | 'medium' | 'poor'>('good');
  const [isDnd, setIsDnd] = useState(false);
  const [reconnectingUsers, setReconnectingUsers] = useState<Record<string, boolean>>({});
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [captions, setCaptions] = useState<
    Array<{ userId: string; username: string; text: string; isFinal: boolean; timestamp: number }>
  >([]);
  const recognitionRef = useRef<any>(null);

  const peers = useRef<Map<string, SimplePeer.Instance>>(new Map());
  const audioEls = useRef<Map<string, HTMLAudioElement>>(new Map());
  const statsIntervalRef = useRef<number | null>(null);
  const turnCredentialsRef = useRef<any>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const reconnectTimerRef = useRef<number | null>(null);
  const isLeavingRef = useRef<boolean>(false);
  const toast = useToast();

  // Monitor connection and auto-disconnect after timeout
  useEffect(() => {
    if (!isInCall) return;

    const checkConnection = setInterval(() => {
      // If socket is connected, keep the call alive and clear any pending reconnect timer
      try {
        if (socketService && socketService.isConnected && socketService.isConnected()) {
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
            try {
              toast.success('Reconnected to server');
            } catch (e) {}
          }
          return;
        }
      } catch (e) {
        // fall through to reconnect logic
      }

      // Socket is not connected: initiate a reconnect wait period (once)
      if (!reconnectTimerRef.current) {
        try {
          toast.info('Reconnecting to server...');
        } catch (e) {}
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null;
          console.warn('Reconnect timeout expired - leaving voice channel');
          try {
            toast.warning('Unable to reconnect — leaving voice channel');
          } catch (e) {}
          leaveVoiceChannel();
        }, RECONNECT_TIMEOUT_MS) as unknown as number;
      }
    }, 1000); // Check every second while in-call to react faster

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      clearInterval(checkConnection);
    };
  }, [isInCall]);

  // Subscribe to socket connection changes to clear reconnect timer immediately on reconnect
  useEffect(() => {
    const unsub = socketService.onConnectionChange((connected) => {
      if (connected && reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
        try {
          toast.success('Reconnected to server');
        } catch (e) {}
      }
    });
    return unsub;
  }, []);

  // Update activity when local speaking is detected (dispatched from VoiceChannel)
  useEffect(() => {
    const handler = () => updateActivity();
    try {
      window.addEventListener('voice:local-activity', handler as EventListener);
    } catch (e) {
      // ignore in non-browser environments
    }
    return () => {
      try {
        window.removeEventListener('voice:local-activity', handler as EventListener);
      } catch (e) {
        // ignore
      }
    };
  }, []);

  // Update activity timestamp when receiving voice events
  const updateActivity = () => {
    lastActivityRef.current = Date.now();
  };

  const fetchTurnCredentials = async () => {
    try {
      const response = await api.get('/voice/turn-credentials');
      turnCredentialsRef.current = response.data;
    } catch (error) {
      console.error('Error fetching TURN credentials:', error);
    }
  };

  const createPeer = (targetUserId: string, initiator: boolean) => {
    if (!localStream) return null as unknown as SimplePeer.Instance;
    const peer = new SimplePeer({
      initiator,
      stream: localStream,
      trickle: true,
      config: {
        iceServers: turnCredentialsRef.current?.iceServers || [
          { urls: ['stun:stun.l.google.com:19302'] },
        ],
      },
    });

    peer.on('signal', (signal: any) => {
      if (initiator) {
        socketService.emit('voice:offer', { targetUserId, offer: signal });
      } else {
        socketService.emit('voice:answer', { targetUserId, answer: signal });
      }
    });

    peer.on('stream', (remoteStream: MediaStream) => {
      try {
        const audio = new Audio();
        audio.autoplay = true;
        audio.srcObject = remoteStream as any;
        audio.play().catch(() => {});
        audioEls.current.set(targetUserId, audio);
      } catch (err) {
        console.warn('Failed to play remote stream', err);
      }
    });

    peer.on('close', () => {
      const a = audioEls.current.get(targetUserId);
      if (a) {
        a.pause();
        a.srcObject = null;
        audioEls.current.delete(targetUserId);
      }
      peers.current.delete(targetUserId);
    });

    peers.current.set(targetUserId, peer);
    return peer;
  };

  const addScreenTrack = (stream: MediaStream) => {
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) return;
    peers.current.forEach((peer) => {
      try {
        peer.addTrack(videoTrack, stream as any);
      } catch (e) {
        // ignore if not supported
      }
    });
  };

  const removeScreenTrack = () => {
    peers.current.forEach((peer) => {
      try {
        const senders = (peer as any)._pc?.getSenders() || [];
        senders.forEach((sender: RTCRtpSender) => {
          if (sender.track?.kind === 'video') {
            (peer as any)._pc?.removeTrack(sender);
          }
        });
      } catch (e) {
        // ignore
      }
    });
  };

  const joinVoiceChannel = async (newChannelId: string, newChannelName: string) => {
    try {
      // Fetch TURN credentials
      await fetchTurnCredentials();

      // Request microphone access with advanced voice isolation and noise suppression
      const constraints = createAudioConstraints({
        noiseSuppressionEnabled: true,
        echoCancellationEnabled: true,
        autoGainControlEnabled: true,
      });
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setLocalStream(stream);

      // Join voice channel via API
      await api.post(`/voice/channels/${newChannelId}/join`);

      // Emit socket event (send channelId string to match backend handler)
      socketService.emit('voice:join', newChannelId);

      // Set state
      setIsInCall(true);
      setChannelId(newChannelId);
      setChannelName(newChannelName);
      sessionStorage.setItem('inVoiceCall', 'true');

      // Play join sound
      // Respect DND / silent mode
      if (!isDnd) soundService.play('join');

      // Update activity
      updateActivity();

      toast.success(`Joined voice channel: ${newChannelName}`);

      // Populate initial users list from server sessions to avoid stale UI state
      try {
        const sessionsResp = await api.get(`/voice/channels/${newChannelId}/sessions`);
        const mapped = sessionsResp.data.sessions.map((s: any) => ({
          id: s.user.id,
          username: s.user.username,
          isMuted: s.isMuted,
          isDeafened: s.isDeafened,
        }));
        setUsers(mapped);
      } catch (err) {
        console.warn('Failed to fetch voice sessions after join:', err);
      }
    } catch (error: any) {
      console.error('Error joining voice channel:', error);
      toast.error('Failed to join voice channel');

      // Cleanup on error: stop the newly acquired media stream directly from the local variable
      try {
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
      } catch (e) {}
      setLocalStream(null);
    }
  };

  const leaveVoiceChannel = async () => {
    if (!channelId || isLeavingRef.current) return;
    isLeavingRef.current = true;

    try {
      // Stop local stream
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
        setLocalStream(null);
      }

      // Destroy all peer connections
      peers.current.forEach((peer) => peer.destroy());
      peers.current.clear();

      // Leave via API
      try {
        await api.post(`/voice/channels/${channelId}/leave`);
      } catch (err: any) {
        // Ignore 404 if not in voice channel
        if (!(err?.response?.status === 404)) {
          throw err;
        }
      }

      // Emit socket event
      socketService.emit('voice:leave', channelId);

      // Play leave sound
      if (!isDnd) soundService.play('leave');

      // Clear state
      setIsInCall(false);
      setChannelId(null);
      setChannelName(null);
      setUsers([]);
      setIsMuted(false);
      setIsDeafened(false);
      setConnectionQuality('good');
      sessionStorage.removeItem('inVoiceCall');

      toast.info('Left voice channel');
    } catch (error) {
      console.error('Error leaving voice channel:', error);
      toast.error('Failed to leave voice channel');
    } finally {
      isLeavingRef.current = false;
    }
  };

  const toggleMute = async () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (!audioTrack) return;
      audioTrack.enabled = !audioTrack.enabled;
      const newMutedState = !audioTrack.enabled;
      setIsMuted(newMutedState);

      // Play mute/unmute sound unless DND
      if (!isDnd) soundService.play(newMutedState ? 'mute' : 'unmute');

      await api.patch('/voice/state', { isMuted: newMutedState });
      updateActivity();
    }
  };

  const toggleDeafen = async () => {
    const newDeafenedState = !isDeafened;
    setIsDeafened(newDeafenedState);
    await api.patch('/voice/state', { isDeafened: newDeafenedState });
    updateActivity();
  };

  // Listen for voice events
  useEffect(() => {
    const handleUserJoined = (data: any) => {
      if (data.channelId === channelId) {
        setUsers((prev) => {
          if (prev.some((u) => String(u.id) === String(data.userId))) return prev;
          return [
            ...prev,
            {
              id: data.userId,
              username: data.username,
              isMuted: false,
              isDeafened: false,
            },
          ];
        });
        // Create peer and initiate handshake if we have a local stream
        try {
          const currentUser = getCachedUser();
          if (currentUser?.id && String(currentUser.id) !== String(data.userId) && localStream) {
            // pick a deterministic initiator rule to avoid double-initiate
            const initiator = String(currentUser.id) < String(data.userId);
            createPeer(data.userId, initiator);
          }
        } catch (err) {
          // ignore
        }
        updateActivity();
      }
    };

    const handleUserLeft = (data: any) => {
      if (data.channelId === channelId) {
        setUsers((prev) => prev.filter((u) => String(u.id) !== String(data.userId)));
        // Destroy peer connection
        const peer = peers.current.get(data.userId);
        if (peer) {
          peer.destroy();
          peers.current.delete(data.userId);
        }
        // Remove reconnecting flag if present
        setReconnectingUsers((prev) => {
          const next = { ...prev };
          delete next[String(data.userId)];
          return next;
        });
        updateActivity();
      }
    };

    const handleTranscription = (data: any) => {
      if (data.channelId === channelId) {
        setCaptions((prev) => {
          // Remove older temporary captions from same user
          const filtered = prev.filter((c) => !(c.userId === data.userId && !c.isFinal));
          return [
            ...filtered,
            {
              userId: data.userId,
              username: data.username,
              text: data.text,
              isFinal: data.isFinal,
              timestamp: Date.now(),
            },
          ].slice(-5); // Keep last 5
        });
      }
    };
    socketService.on('voice:transcription', handleTranscription);
    const handleSpeaking = () => {
      updateActivity();
    };

    socketService.on('voice:user-joined', handleUserJoined);
    socketService.on('voice:user-left', handleUserLeft);
    socketService.on('voice:speaking', handleSpeaking);

    const handleUserReconnecting = (data: any) => {
      // Show reconnecting badge for this user
      setReconnectingUsers((prev) => ({ ...prev, [String(data.userId)]: true }));
      updateActivity();
    };

    const handleUserRestored = (data: any) => {
      setReconnectingUsers((prev) => {
        const next = { ...prev };
        delete next[String(data.userId)];
        return next;
      });
      updateActivity();
    };

    socketService.on('voice:user-reconnecting', handleUserReconnecting);
    socketService.on('voice:user-restored', handleUserRestored);

    // Signaling handlers
    const handleOffer = (data: any) => {
      updateActivity();
      // data: { fromUserId, offer }
      const from = data.fromUserId;
      let peer = peers.current.get(from);
      if (!peer) {
        peer = createPeer(from, false);
      }
      try {
        peer?.signal(data.offer);
      } catch (e) {
        console.warn(e);
      }
    };

    const handleAnswer = (data: any) => {
      updateActivity();
      const from = data.fromUserId;
      const peer = peers.current.get(from);
      if (peer) {
        try {
          peer.signal(data.answer);
        } catch (e) {
          console.warn(e);
        }
      }
    };

    const handleIceCandidate = (data: any) => {
      updateActivity();
      const from = data.fromUserId;
      const peer = peers.current.get(from);
      if (peer) {
        try {
          peer.signal(data.candidate);
        } catch (e) {
          console.warn(e);
        }
      }
    };

    socketService.on('voice:offer', handleOffer);
    socketService.on('voice:answer', handleAnswer);
    socketService.on('voice:ice-candidate', handleIceCandidate);

    return () => {
      socketService.off('voice:user-joined', handleUserJoined);
      socketService.off('voice:user-left', handleUserLeft);
      socketService.off('voice:speaking', handleSpeaking);
      socketService.off('voice:transcription', handleTranscription);
      socketService.off('voice:offer', handleOffer);
      socketService.off('voice:answer', handleAnswer);
      socketService.off('voice:ice-candidate', handleIceCandidate);
      socketService.off('voice:user-reconnecting', handleUserReconnecting);
      socketService.off('voice:user-restored', handleUserRestored);
    };
  }, [channelId]);

  // Device change handler - attempt to recover if microphone device disappears
  useEffect(() => {
    const handleDeviceChange = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasAudioInput = devices.some((d) => d.kind === 'audioinput');
        if (!hasAudioInput && localStream) {
          // No inputs available - inform user
          toast.warning('Microphone device removed. Attempting to recover...');
        }

        // If we have a localStream, ensure its device still exists; if not, try to re-acquire
        if (localStream) {
          const track = localStream.getAudioTracks()[0];
          const settings: any = track.getSettings ? track.getSettings() : {};
          const deviceId = settings.deviceId;
          if (deviceId && !devices.some((d) => d.deviceId === deviceId)) {
            toast.info('Selected microphone was removed. Re-acquiring microphone...');
            try {
              const newConstraints = createAudioConstraints({
                noiseSuppressionEnabled: true,
                echoCancellationEnabled: true,
                autoGainControlEnabled: true,
              });
              const newStream = await navigator.mediaDevices.getUserMedia(newConstraints);
              // Replace tracks on peers
              localStream.getTracks().forEach((t) => t.stop());
              setLocalStream(newStream);
              peers.current.forEach((peer) => {
                try {
                  peer.replaceTrack?.(track, newStream.getAudioTracks()[0], localStream);
                } catch (e) {
                  // ignore if replaceTrack not supported
                }
              });
              toast.success('Microphone re-acquired');
            } catch (err) {
              toast.error('Failed to re-acquire microphone');
            }
          }
        }
      } catch (err) {
        console.warn('Error handling devicechange:', err);
      }
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
  }, [localStream, toast]);

  // Stats polling for peers to compute simple connection quality
  useEffect(() => {
    if (!isInCall) return;

    const pollStats = async () => {
      try {
        let worst = 'good';
        for (const [, peer] of peers.current.entries()) {
          // Access underlying RTCPeerConnection if available
          // @ts-ignore
          const pc = peer._pc as RTCPeerConnection | undefined;
          if (pc && pc.getStats) {
            const stats = await pc.getStats();
            stats.forEach((report: any) => {
              if (report.type === 'inbound-rtp' && report.kind === 'audio') {
                const packetsLost = report.packetsLost || 0;
                const jitter = report.jitter || 0;
                if (packetsLost > 50 || jitter > 0.15) worst = 'poor';
                else if (packetsLost > 10 || jitter > 0.08)
                  worst = worst === 'poor' ? 'poor' : 'medium';
              }
            });
          }
        }
        setConnectionQuality(worst as 'good' | 'medium' | 'poor');
      } catch (err) {
        console.warn('Error polling stats:', err);
      }
    };

    statsIntervalRef.current = window.setInterval(pollStats, 15000);
    // run immediately
    pollStats();

    return () => {
      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current);
        statsIntervalRef.current = null;
      }
    };
  }, [isInCall]);

  // In-app DND toggle - mutes sounds but keeps voice call active
  const toggleDnd = (enable?: boolean) => {
    const newState = enable !== undefined ? enable : !isDnd;
    setIsDnd(newState);
    soundService.toggleSilentMode(newState);
    // Notify other components
    window.dispatchEvent(new CustomEvent('dnd:changed', { detail: { enabled: newState } }));
  };

  // Auto-clear old captions
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setCaptions((prev) => prev.filter((c) => now - c.timestamp < 10000)); // Keep for 10 seconds
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Handle local speech recognition for captions
  useEffect(() => {
    if (isInCall && captionsEnabled && !isMuted) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'en-US';

        recognition.onresult = (event: any) => {
          const result = event.results[event.results.length - 1];
          const text = result[0].transcript;
          const isFinal = result.isFinal;

          // Only send if we have a valid channel
          if (channelId) {
            socketService.emit('voice:transcription', {
              channelId,
              text,
              isFinal,
            });
          }
        };

        recognition.onerror = (event: any) => {
          console.warn('Speech recognition error:', event.error);
          // Try to restart on certain errors
          if (event.error === 'no-speech' || event.error === 'network') {
            try {
              recognition.stop();
            } catch (e) {}
            setTimeout(() => {
              try {
                recognition.start();
              } catch (e) {}
            }, 1000);
          }
        };

        recognition.onend = () => {
          // Auto-restart if still enabled
          if (captionsEnabled && isInCall && !isMuted) {
            try {
              recognition.start();
            } catch (e) {}
          }
        };

        try {
          recognition.start();
          recognitionRef.current = recognition;
        } catch (e) {
          console.error('Failed to start speech recognition:', e);
        }
      } else {
        toast.warning('Speech recognition is not supported in your browser.');
        setCaptionsEnabled(false);
      }
    } else if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
      recognitionRef.current = null;
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
        recognitionRef.current = null;
      }
    };
  }, [isInCall, captionsEnabled, isMuted, channelId]);

  const toggleCaptions = () => {
    setCaptionsEnabled((prev) => !prev);
  };

  const value: VoiceCallContextType = {
    isInCall,
    channelId,
    channelName,
    isMuted,
    isDeafened,
    users,
    connectionQuality,
    joinVoiceChannel,
    leaveVoiceChannel,
    captionsEnabled,
    captions,
    toggleCaptions,
    toggleMute,
    toggleDeafen,
    localStream,
    isDnd,
    toggleDnd,
    addScreenTrack,
    removeScreenTrack,
    reconnectingUsers,
  };

  return <VoiceCallContext.Provider value={value}>{children}</VoiceCallContext.Provider>;
}
